export const RETOUCH_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
export const RETOUCH_REQUIRED_ASPECT_RATIO = 1.33;
export const RETOUCH_ACCEPT_MIME = 'image/jpeg,image/png,image/jpg,image/gif';

import { parseRetouchFileName } from './retouchFileNameParser';

export type RetouchFileValidationErrorCode =
    | 'maxFileSize'
    | 'invalidAspectRatio'
    | 'invalidFormat'
    | 'dimensionReadFailed'
    | 'invalidFileName'
    | 'missingFrameInfo';

export interface RetouchFileValidationResult {
    valid: boolean;
    errorCode?: RetouchFileValidationErrorCode;
    width?: number;
    height?: number;
    ratio?: number;
}

/** C# Math.Round(value, 2, MidpointRounding.AwayFromZero) ile uyumlu yuvarlama. */
function roundAwayFromZero(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    const scaled = value * factor;
    if (scaled >= 0) {
        return Math.floor(scaled + 0.5) / factor;
    }
    return Math.ceil(scaled - 0.5) / factor;
}

function getFileExtension(fileName: string): string {
    const dot = fileName.lastIndexOf('.');
    if (dot < 0) {
        return '';
    }
    return fileName.slice(dot).toLowerCase();
}

function isAllowedRetouchFormat(file: File): boolean {
    const ext = getFileExtension(file.name);
    if (RETOUCH_ALLOWED_EXTENSIONS.has(ext)) {
        return true;
    }

    const type = file.type?.toLowerCase();
    return type === 'image/jpeg' || type === 'image/jpg' || type === 'image/png' || type === 'image/gif';
}

async function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
    if (typeof createImageBitmap === 'function') {
        try {
            const bitmap = await createImageBitmap(file);
            const dimensions = { width: bitmap.width, height: bitmap.height };
            bitmap.close();
            return dimensions;
        } catch {
            // Image() fallback
        }
    }

    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };

        img.src = url;
    });
}

const RETOUCH_ALLOWED_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.gif']);

export async function validateRetouchFile(file: File): Promise<RetouchFileValidationResult> {
    const nameParse = parseRetouchFileName(file.name);
    if (!nameParse.success) {
        if (nameParse.errorCode === 'missing_frame_info') {
            return { valid: false, errorCode: 'missingFrameInfo' };
        }
        return { valid: false, errorCode: 'invalidFileName' };
    }

    if (!isAllowedRetouchFormat(file)) {
        return { valid: false, errorCode: 'invalidFormat' };
    }

    if (file.size > RETOUCH_MAX_FILE_SIZE_BYTES) {
        return { valid: false, errorCode: 'maxFileSize' };
    }

    try {
        const { width, height } = await readImageDimensions(file);
        if (width <= 0 || height <= 0) {
            return { valid: false, errorCode: 'dimensionReadFailed' };
        }

        const rawRatio = height / width;
        const ratio = roundAwayFromZero(rawRatio, 2);
        if (ratio !== RETOUCH_REQUIRED_ASPECT_RATIO) {
            return { valid: false, errorCode: 'invalidAspectRatio', width, height, ratio };
        }

        return { valid: true, width, height, ratio };
    } catch {
        return { valid: false, errorCode: 'dimensionReadFailed' };
    }
}
